import { sanitizeInput } from '@shared/utils/sanitize-input/sanitizeInput';

import type { User } from '../../api/types/user.types';
import type {
  RoleOption,
  RoleValue,
  UserDetailMode,
  UserFormData,
} from './UserDetailPanel.types';

export const ROLE_OPTIONS: RoleOption[] = [
  {
    value: 'Admin',
    label: 'Admin',
    description:
      "Fully control of company's accesses and data for records, training information, documents and billing.",
  },
  {
    value: 'Training',
    label: 'Training',
    description:
      'View and manage bookings, provide data for training and access documents.',
  },
  {
    value: 'Records',
    label: 'Records',
    description: 'View, download and manage crew records.',
  },
  {
    value: 'Finance',
    label: 'Finance',
    description:
      'View invoices, make payments and access other financial functions.',
  },
];

export function getInitialFormData(
  mode: UserDetailMode,
  user?: User,
  initialData?: Partial<UserFormData>
): UserFormData {
  if (mode === 'add' || mode === 'sf-add' || !user) {
    const defaults: UserFormData = {
      firstName: '',
      middleName: '',
      lastName: '',
      email: '',
      status: 'Active',
      roles: [],
    };

    if ((mode === 'add' || mode === 'sf-add') && initialData) {
      return { ...defaults, ...initialData };
    }

    return defaults;
  }

  return {
    firstName: user.firstName,
    middleName: user.middleName ?? '',
    lastName: user.lastName,
    email: user.email,
    status: user.status,
    roles: expandRolesForDisplay([...user.roles]),
  };
}

export function hasFormChanges(
  current: UserFormData,
  initial: UserFormData
): boolean {
  if (current.firstName !== initial.firstName) return true;
  if (current.middleName !== initial.middleName) return true;
  if (current.lastName !== initial.lastName) return true;
  if (current.email !== initial.email) return true;
  if (current.status !== initial.status) return true;

  if (current.roles.length !== initial.roles.length) return true;

  const sortedCurrent = [...current.roles].sort();
  const sortedInitial = [...initial.roles].sort();

  return sortedCurrent.some((role, index) => role !== sortedInitial[index]);
}

export function sanitizeFormData(data: UserFormData): UserFormData {
  return {
    firstName: sanitizeInput(data.firstName) ?? '',
    middleName: sanitizeInput(data.middleName) ?? '',
    lastName: sanitizeInput(data.lastName) ?? '',
    email: sanitizeInput(data.email) ?? '',
    status: data.status,
    roles: data.roles,
  };
}

export function isAdminSelected(roles: string[]): boolean {
  return roles.includes('Admin');
}

export const SUBORDINATE_ROLES: readonly RoleValue[] = [
  'Training',
  'Records',
  'Finance',
];

/**
 * Expands roles for display in the form.
 * If Admin is present, adds all subordinate roles so all checkboxes appear checked.
 * Mirrors the behaviour of clicking Admin in the UI.
 */
export function expandRolesForDisplay(roles: string[]): string[] {
  if (roles.includes('Admin')) {
    const toAdd = SUBORDINATE_ROLES.filter(r => !roles.includes(r));
    return [...roles, ...toAdd];
  }
  return [...roles];
}

/**
 * Serializes roles for the API.
 * If Admin is present, only sends ['Admin'] — the backend owns the Admin → subordinate mapping.
 */
export function serializeRolesForApi(roles: string[]): string[] {
  if (roles.includes('Admin')) return ['Admin'];
  return [...roles];
}

export function computeRolesAfterToggle(
  currentRoles: string[],
  toggledRole: RoleValue
): string[] {
  const isChecked = currentRoles.includes(toggledRole);

  if (toggledRole === 'Admin') {
    if (isChecked) {
      return currentRoles.filter(r => r !== 'Admin');
    }
    const toAdd = SUBORDINATE_ROLES.filter(r => !currentRoles.includes(r));
    return ['Admin', ...currentRoles, ...toAdd];
  }

  if (isChecked) {
    return currentRoles.filter(r => r !== toggledRole && r !== 'Admin');
  }

  return [...currentRoles, toggledRole];
}

export const CREATE_USER_ERROR_TITLE = 'Creating user failed.';
export const UPDATE_USER_ERROR_TITLE = 'Updating user failed.';
export const ERROR_RETRY_MESSAGE = 'Please try again.';
